import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import { contracts, publish, watch } from '@arcotech-services/mfe-communicator'

import { jwtDecode } from 'jwt-decode'
import Keycloak from 'keycloak-js'

import { isFederation, isProdEnv } from '../../helpers/environment'
import { useSessionStore } from '../../stores/session'

const keycloak = new Keycloak({
  clientId: 'backoffice-login',
  realm: 'backoffice',
  url: isProdEnv
    ? 'https://keycloak.arcotech.io/auth'
    : 'https://keycloak-stg.arcotech.io/auth'
})

const redirectUri = window.location.href

type JwtPayload = {
  email: string
  given_name: string
  name: string
  sub: string
}

const SessionDev: React.FC<PropsWithChildren> = ({ children }) => {
  const [isReady, setIsReady] = useState(isFederation)
  const [isAuthenticated, setIsAuthenticated] = useState(isFederation)
  const isInitializedRef = useRef(isFederation)
  const isWatchInitRef = useRef(false)
  const sessionDataRef = useRef<{
    accessToken: string
    username: string
    email: string
    name: string
    id: string
  } | null>(null)

  const setAuthData = useSessionStore(state => state.setAuthData)
  const setBusinessUnit = useSessionStore(state => state.setBusinessUnit)
  const setUserInfo = useSessionStore(state => state.setUserInfo)

  const publishAuth = useCallback((accessToken: string) => {
    publish(contracts.backoffice.request.auth, {
      accessToken,
      isExpired: false
    })
  }, [])

  const publishUserInfo = useCallback(
    (username: string, email: string, name: string, id: string) => {
      publish(contracts.backoffice.request.userInfo, {
        username,
        email,
        name,
        id,
        isActive: true
      })
    },
    []
  )

  const publishBusinessUnits = useCallback(() => {
    publish(contracts.backoffice.request.businessUnits, {
      currentUnit: {
        id: 1,
        code: 'SAS',
        name: 'SAS',
        realmName: 'backoffice'
      },
      units: []
    })
  }, [])

  const publishUserPermissions = useCallback(() => {
    publish(contracts.backoffice.request.userPermissions, [])
  }, [])

  const registerWatchers = useCallback(() => {
    if (isWatchInitRef.current || !sessionDataRef.current) {
      return
    }

    isWatchInitRef.current = true
    const { accessToken, username, email, name, id } = sessionDataRef.current

    watch(contracts.backoffice.request.auth, () => publishAuth(accessToken))
    watch(contracts.backoffice.request.userInfo, () =>
      publishUserInfo(username, email, name, id)
    )
    watch(contracts.backoffice.request.businessUnits, () =>
      publishBusinessUnits()
    )
    watch(contracts.backoffice.request.userPermissions, () =>
      publishUserPermissions()
    )
  }, [
    publishAuth,
    publishUserInfo,
    publishBusinessUnits,
    publishUserPermissions
  ])

  const updateTokenInSession = useCallback(() => {
    if (!keycloak.token || !sessionDataRef.current) {
      return
    }

    const accessToken = keycloak.token
    sessionDataRef.current.accessToken = accessToken
    publishAuth(accessToken)

    if (!isFederation) {
      setAuthData({ accessToken, isExpired: false })
    }
  }, [publishAuth, setAuthData])

  const handleSession = useCallback(() => {
    if (!keycloak.token) {
      return
    }

    setIsAuthenticated(true)

    const {
      name: username,
      email,
      given_name: name,
      sub: id
    } = jwtDecode<JwtPayload>(keycloak.token)

    const accessToken = keycloak.token

    sessionDataRef.current = { accessToken, username, email, name, id }

    publishAuth(accessToken)
    publishUserInfo(username, email, name, id)
    publishBusinessUnits()
    publishUserPermissions()

    if (!isFederation) {
      setAuthData({ accessToken, isExpired: false })
      setBusinessUnit({
        currentUnit: { id: 1, code: 'SAS', name: 'SAS', realmName: 'sas' },
        units: []
      })
      setUserInfo({ username, email, name, id })
    }

    registerWatchers()
  }, [
    publishAuth,
    publishUserInfo,
    publishBusinessUnits,
    publishUserPermissions,
    registerWatchers,
    setAuthData,
    setBusinessUnit,
    setUserInfo
  ])

  const handleTokenRefreshSuccess = useCallback(() => {
    if (keycloak.token && sessionDataRef.current) {
      updateTokenInSession()
    } else {
      handleSession()
    }
  }, [handleSession, updateTokenInSession])

  useEffect(() => {
    if (keycloak.authenticated && keycloak.didInitialize) {
      handleSession()
      return
    }

    if (isInitializedRef.current) {
      return
    }

    isInitializedRef.current = true

    keycloak
      .init({ flow: 'implicit', onLoad: 'login-required', redirectUri })
      .then((isAuth: boolean) => {
        setIsReady(true)
        if (isAuth) {
          handleSession()
        }
      })
      .catch((error: unknown) => {
        console.error('Error initializing Keycloak:', error)
      })
  }, [handleSession])

  useEffect(() => {
    if (!isReady) {
      return
    }

    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken()
        .then((isAuth: boolean) => {
          if (isAuth) {
            handleTokenRefreshSuccess()
          }
        })
        .catch((error: unknown) => {
          console.error('Error updating token:', error)
        })
    }
  }, [isReady, handleTokenRefreshSuccess])

  useEffect(() => {
    if (!isAuthenticated && isReady) {
      keycloak.login({ redirectUri }).catch((error: unknown) => {
        console.error('Error during login:', error)
      })
    }
  }, [isAuthenticated, isReady])

  return isAuthenticated ? children : <p>Carregando...</p>
}

export default SessionDev
